// Elintra — Dashboard Layout | v3.2
// FIX: NAV_ITEMS 'app/analytics' label 'AEO Analytics' → 'Analytics' (canonical v3.2 name).

import { useState } from 'react';
import {
  Activity, BarChart3, Database, LogOut, Menu,
  Settings, ShieldCheck, Terminal, X, Zap,
} from 'lucide-react';
import { User as UserIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge }  from '@/components/ui/Badge';
import type { DashboardLayoutProps } from '@/types/components.types';
import type { Route } from '@/types/app';

// FIX: 'AEO Analytics' → 'Analytics' — canonical v3.2 name
const NAV_ITEMS: Array<{ route: Route; label: string; icon: React.ElementType }> = [
  { route: 'app',           label: 'Overview',       icon: Activity    },
  { route: 'app/analytics', label: 'Analytics',      icon: BarChart3   },
  { route: 'app/log',       label: 'Agent Log',      icon: Terminal    },
  { route: 'app/stores',    label: 'Stores',         icon: Database    },
  { route: 'app/protocols', label: 'Protocol Health',icon: ShieldCheck },
];

export const DashboardLayout = ({
  children, title, currentRoute, onNavigate, onLogout, user,
}: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleLogout = () => (onLogout ? onLogout() : onNavigate('' as Route));

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex">

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className={cn(
        'fixed md:sticky top-0 left-0 z-40 h-screen w-64',
        'border-r border-zinc-800 bg-zinc-950 flex flex-col',
        'transition-transform duration-300 md:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
      )}>

        {/* Logo */}
        <div className="h-16 border-b border-zinc-800 flex items-center px-6 gap-2.5 shrink-0">
          <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center
                          shadow-[0_0_15px_rgba(139,92,246,0.5)]">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-lg tracking-tight">Elintra</span>
          <button type="button"
            className="ml-auto md:hidden text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer"
            onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wider px-3 py-2 mt-2">
            Control Plane
          </p>
          {NAV_ITEMS.map(({ route, label, icon: Icon }) => {
            const active = currentRoute === route;
            return (
              <button key={route} type="button"
                onClick={() => { onNavigate(route); setSidebarOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer',
                  'text-sm font-medium transition-all duration-150 border',
                  active
                    ? 'bg-violet-500/10 text-violet-400 border-violet-500/20'
                    : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/80 border-transparent',
                )}>
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </button>
            );
          })}

          <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wider px-3 py-2 mt-4">
            Account
          </p>
          <button type="button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                       text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/80
                       transition-all duration-150 border border-transparent cursor-pointer">
            <Settings className="w-4 h-4 shrink-0" />
            Settings
          </button>
        </nav>

        {/* User Footer */}
        <div className="border-t border-zinc-800 p-4 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-violet-500/20 border border-violet-500/30
                            flex items-center justify-center shrink-0">
              <UserIcon className="w-4 h-4 text-violet-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.email ?? 'creator@elintra.dev'}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Badge variant="primary" className="text-[10px] px-1.5 py-0 capitalize">
                  {user?.planTier ?? 'creator'}
                </Badge>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm"
            className="w-full justify-start text-zinc-500 hover:text-zinc-300 gap-2"
            onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
            Exit to home
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="h-16 border-b border-zinc-800 flex items-center
                           justify-between px-6 bg-zinc-950/80 backdrop-blur-sm shrink-0">
          <div className="flex items-center gap-4">
            <button type="button"
              className="md:hidden text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer"
              onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-semibold text-base">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="success" className="gap-1.5 hidden sm:flex">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              UAG v3.0.0
            </Badge>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
